import {
  MonitoringPromptStatus,
  MonitoringPromptType,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientMonitoringPromptEntity,
  MonitoringPromptListFilters,
  MonitoringPromptTermAssociationOperation,
  MonitoringPromptTermAssociationType,
} from '@edgebox/sync-core-rest-client';

/** Prompt statuses an operator can act on. A previous revision is history, not an editing surface. */
export const NON_PREVIOUS_PROMPT_STATUSES = [
  MonitoringPromptStatus.Draft,
  MonitoringPromptStatus.Active,
  MonitoringPromptStatus.Paused,
  MonitoringPromptStatus.Archived,
];

export interface ITermPromptAssociationSection {
  type: MonitoringPromptTermAssociationType;
  /** Section heading. */
  label: string;
  /** One line saying what associating a term here actually does. */
  description: string;
  /**
   * Whether adding or removing this association rewrites term assignments on the prompt's existing
   * runs. Only the static association does, and the operator is told before they act.
   */
  rewritesRunHistory: boolean;
}

/**
 * The four fields a taxonomy term can be associated with a monitoring prompt through.
 *
 * Order is the order the sections render in, from the most administrative to the most generative.
 */
export const TERM_PROMPT_ASSOCIATION_SECTIONS: ITermPromptAssociationSection[] = [
  {
    type: MonitoringPromptTermAssociationType.Administrative,
    label: 'Administrative',
    description: 'Classifies the prompt itself — purpose, persona, funnel stage, intent. Does not change what is measured.',
    rewritesRunHistory: false,
  },
  {
    type: MonitoringPromptTermAssociationType.ExtractionTarget,
    label: 'Extraction targets',
    description: 'What attribution tracks in the answers this prompt produces.',
    rewritesRunHistory: false,
  },
  {
    type: MonitoringPromptTermAssociationType.Static,
    label: 'Static',
    description: 'Stamped onto every run of the prompt, so term-filtered statistics can be sliced by it.',
    rewritesRunHistory: true,
  },
  {
    type: MonitoringPromptTermAssociationType.Templated,
    label: 'Templated',
    description:
      'The matrix a template iterates: one prompt instance is generated per term. Removing a term archives the instances it generated; their runs are kept.',
    rewritesRunHistory: false,
  },
];

/**
 * The facets a prompt measures something about.
 *
 * These are the terms attribution tracks in an answer and the terms a run is stamped with, which is
 * what the extraction-target and static fields hold. Read off the same lists the AI-visibility
 * screens classify by (`OWN_FACETS` and `COMPETITOR_FACETS`), so "a monitoring subject" means one
 * thing across the product.
 */
export const SUBJECT_PROMPT_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Brand,
  TaxonomyFacet.Product,
  TaxonomyFacet.Program,
  TaxonomyFacet.Site,
  TaxonomyFacet.Initiative,
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.CompetitorProgram,
];

/**
 * The facets that classify a prompt rather than being measured by it.
 *
 * Purpose, persona, funnel stage and intent are what the administrative field holds: they say what
 * a prompt is for, and change nothing about what it measures.
 */
export const ADMINISTRATIVE_PROMPT_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.MonitoringPromptPurpose,
  TaxonomyFacet.Persona,
  TaxonomyFacet.FunnelStage,
  TaxonomyFacet.Intent,
];

/**
 * The association sections a term of this facet can actually appear in.
 *
 * Every other facet's terms are absent from all four prompt fields by construction, so rendering
 * the sections for them produced four queries and four "no prompts use this term here yet" panels
 * that could never say anything else — a page telling the reader nothing, four times over. The
 * clearest case is `engine`: a prompt's engines live in their own field, which none of these four
 * filters queries, so an engine term is structurally absent from all of them.
 *
 * Templated is not restricted to one of the two groups. A template substitutes one facet into one
 * placeholder and `associationPromptEligibility` matches the term's facet against the template's,
 * so whether there is anything to show is a question about the project's templates rather than
 * about the facet — the section is offered wherever the term can be a prompt subject or classifier
 * at all, and says so itself when no template iterates that facet.
 */
export function termPromptSections(facet?: TaxonomyFacet): ITermPromptAssociationSection[] {
  if (!facet) {
    return [];
  }

  const applicable = new Set<MonitoringPromptTermAssociationType>();

  if (ADMINISTRATIVE_PROMPT_FACETS.includes(facet)) {
    applicable.add(MonitoringPromptTermAssociationType.Administrative);
    applicable.add(MonitoringPromptTermAssociationType.Templated);
  }

  if (SUBJECT_PROMPT_FACETS.includes(facet)) {
    applicable.add(MonitoringPromptTermAssociationType.ExtractionTarget);
    applicable.add(MonitoringPromptTermAssociationType.Static);
    applicable.add(MonitoringPromptTermAssociationType.Templated);
  }

  // Catalogue order, whatever order the rules above added them in, so two facets that share a
  // section always read it in the same place.
  return TERM_PROMPT_ASSOCIATION_SECTIONS.filter((section) => applicable.has(section.type));
}

/** Whether a term of this facet has a Prompts tab at all. */
export function facetCarriesPrompts(facet?: TaxonomyFacet): boolean {
  return termPromptSections(facet).length > 0;
}

export function associationSection(type: MonitoringPromptTermAssociationType): ITermPromptAssociationSection {
  const section = TERM_PROMPT_ASSOCIATION_SECTIONS.find((candidate) => candidate.type === type);
  if (!section) {
    throw new Error(`Unknown monitoring prompt term association type: ${type}`);
  }
  return section;
}

/**
 * The list filter that finds the prompts holding `termKey` in this association's field.
 *
 * Each association type maps to its own server-side filter, so a section never has to post-filter a
 * page it received — which would silently under-report as soon as there is more than one page.
 */
export function associationListFilter(type: MonitoringPromptTermAssociationType, termKey: string): MonitoringPromptListFilters {
  const base: MonitoringPromptListFilters = { status: NON_PREVIOUS_PROMPT_STATUSES };

  switch (type) {
    case MonitoringPromptTermAssociationType.Administrative:
      return { ...base, termsKey: termKey };
    case MonitoringPromptTermAssociationType.ExtractionTarget:
      return { ...base, targetTermsKey: termKey };
    case MonitoringPromptTermAssociationType.Static:
      return { ...base, staticTermsKey: termKey };
    case MonitoringPromptTermAssociationType.Templated:
      return { ...base, templateTermsKey: termKey };
    default:
      throw new Error(`Unknown monitoring prompt term association type: ${type}`);
  }
}

/**
 * Which prompts a picker may offer for one association type, and what to say when none qualify.
 *
 * The rules are not cosmetic. A template-generated instance is refused by the association endpoint
 * outright; a templated term written onto a prompt that is not a template is accepted and generates
 * nothing; a templated term whose facet differs from the template's is refused, because the
 * template substitutes exactly one facet into exactly one placeholder.
 */
export interface IMonitoringPromptEligibility {
  /**
   * Server-side `type` filter for the list query. Absent means no type restriction.
   *
   * This never has to exclude template-generated instances: the list endpoint drops them unless a
   * `templatePromptKey` filter asks for them, and `isTemplateGeneratedInstance` re-checks the rows
   * that do arrive so the picker does not depend on that default staying put.
   */
  types?: MonitoringPromptType[];
  /**
   * Only templates that iterate this facet can substitute the term.
   *
   * The list endpoint has no `templateFacet` filter, so this one can only be applied in the browser
   * — which is why a picker carrying it holds the whole eligible set rather than one server page.
   */
  templateFacet?: TaxonomyFacet;
  /** What to tell the operator when nothing qualifies, instead of an empty list that reads as a failure. */
  emptyExplanation: string;
}

/**
 * A prompt generated from a template.
 *
 * `type` alone does not identify one: a standalone prompt is `Instance` too, it just has no
 * template above it. The distinguishing fact is the template reference. Their term fields are
 * rewritten from the template on every reconcile, so the association endpoint rejects the whole
 * request rather than accept one — offering them can only produce a failed save.
 */
export function isTemplateGeneratedInstance(prompt: ClientMonitoringPromptEntity): boolean {
  return prompt.type === MonitoringPromptType.Instance && !!prompt.templatePrompt?.getStableKey?.();
}

/** Whether this prompt may be offered under an eligibility rule. */
export function isEligiblePrompt(prompt: ClientMonitoringPromptEntity, eligibility?: IMonitoringPromptEligibility): boolean {
  if (isTemplateGeneratedInstance(prompt)) {
    return false;
  }

  if (eligibility?.templateFacet) {
    return prompt.templateFacet === eligibility.templateFacet;
  }

  return true;
}

function facetLabel(facet: TaxonomyFacet): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyFacet', facet);
}

/**
 * The prompts an operator may pick for one association type on a term of `termFacet`.
 *
 * Templated is the narrow one. It means "substitute this term into the template's placeholder", so
 * a prompt that is not a template has nothing to substitute, and a template of another facet
 * declares a different placeholder — the server infers the facet from the associated terms and
 * refuses a mismatch. Matching on an exact, defined facet is not an approximation of that rule but
 * the rule itself: a template carrying `templateFacet` is guaranteed to carry the matching
 * placeholder as well, and a template without a facet can carry no placeholder at all.
 *
 * The other three fields classify or stamp the prompt rather than generate from it, so any root
 * prompt qualifies.
 */
export function associationPromptEligibility(
  type: MonitoringPromptTermAssociationType,
  termFacet?: TaxonomyFacet
): IMonitoringPromptEligibility {
  if (type !== MonitoringPromptTermAssociationType.Templated) {
    return { emptyExplanation: 'This project has no prompts this term could be assigned to.' };
  }

  if (!termFacet) {
    return {
      types: [MonitoringPromptType.Template],
      emptyExplanation:
        "This term's taxonomy facet is unknown, so the templates it could be substituted into cannot be determined. Open the term from its taxonomy and try again.",
    };
  }

  return {
    types: [MonitoringPromptType.Template],
    templateFacet: termFacet,
    emptyExplanation: `No template in this project iterates the ${facetLabel(
      termFacet
    )} facet, so there is nowhere to substitute this term. A template has to declare that facet and carry its placeholder before it can use the term.`,
  };
}

export interface IAssociationResultLike {
  changed: boolean;
  runsModified?: number | null;
}

/**
 * What to tell the operator after a bulk call, from the per-pair results the server returned.
 *
 * Unchanged pairs are reported rather than hidden: "already associated" is a real answer to "did my
 * click do anything", and silently counting them as successes would make a no-op look like a write.
 * Run counts are only mentioned when the server actually rewrote runs, so a section that does not
 * touch history never claims it did.
 */
export function summarizeAssociationResults(
  results: IAssociationResultLike[],
  operation: MonitoringPromptTermAssociationOperation
): string {
  if (!results.length) {
    return 'Nothing to do.';
  }

  const changed = results.filter((result) => result.changed).length;
  const unchanged = results.length - changed;
  const verb = operation === MonitoringPromptTermAssociationOperation.Add ? 'Added' : 'Removed';
  const pairs = (count: number) => `${count} ${count === 1 ? 'association' : 'associations'}`;

  const parts: string[] = [];
  parts.push(changed ? `${verb} ${pairs(changed)}.` : `No change: ${pairs(unchanged)} already in that state.`);

  if (changed && unchanged) {
    parts.push(`${pairs(unchanged)} already in that state.`);
  }

  const runsModified = results.reduce((total, result) => total + (result.runsModified ?? 0), 0);
  if (runsModified > 0) {
    parts.push(`${runsModified} existing ${runsModified === 1 ? 'run' : 'runs'} updated.`);
  }

  return parts.join(' ');
}
